import React, { useEffect, useState } from 'react';
import { Box, Button as MuiButton, TextField, FormControl, InputLabel, Select, MenuItem, Typography, List, ListItem, ListItemText, IconButton, Modal, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateTime } from 'luxon';
import { Button } from 'reactstrap';
import { Colors } from '../../Colors';
import styled from 'styled-components';
import { formatIsoToCustomDateStringWithEEEE } from '../../utils';
import { Organization, ShiftOptions, Choices, FrontendWaitlistPatient } from '../../types';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import LoadingWithSubtitle from '../subcomponents/LoadingWithSubtitle';
import AddIcon from '@mui/icons-material/Add';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateRangePicker } from '@mui/x-date-pickers-pro';

const StyledButton = styled(MuiButton)`
  padding-top: 10px;
  margin-top: 16px;
  background-color: #1976d2;
  color: white;

  &:hover {
    background-color: #115293;
  }
`;

interface EditPatientModalProps {
  open: boolean;
  onClose: (open: boolean) => void;
  onSuccess: () => void;
  patient: FrontendWaitlistPatient;
  setPatient: (patient: FrontendWaitlistPatient) => void;
  errorMessage: string;
  loading: boolean;
}

const StyledAddDateButton = styled(Button)`
  background-color: #1976d2;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  text-transform: none;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.24);
  &:hover {
    background-color: #115293;
  }
  &:active {
    background-color: #0d3c6e;
  }
  &:disabled {
    background-color: #cccccc;
    color: #666666;
  }
`;

const EditPatientModal: React.FC<EditPatientModalProps> = ({
  open, onClose, onSuccess, patient, setPatient, errorMessage, loading
}) => {
  const organization: Organization | null = useSelector((state: RootState) => state.organization.organization);
  const [physicianNames, setPhysicianNames] = useState<string[]>(organization?.physicianNames || []);
  const [localErrorMessage, setLocalErrorMessage] = useState<string>('');
  const [dateRange, setDateRange] = useState<[DateTime | null, DateTime | null]>([null, null]);
  const [firstDate, setFirstDate] = useState<DateTime | null>(null);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [value, setValue] = useState<[DateTime | null, DateTime | null]>([null, null]);

  useEffect(() => {
    return () => {
      setDateRange([null, null]);
    };
  }, []);

  useEffect(() => {
    if (!organization) return;
    setPhysicianNames(organization?.physicianNames || {});
  }, [organization?.physicianNames]);

  const handleCalendarChange = (newValue: [DateTime | null, DateTime | null]) => {
    console.log("newValue: ", newValue);
    setValue(newValue);

    if (newValue[0] && !newValue[1]) {
      // User selects only the start date
      setFirstDate(newValue[0]);
    } else if (newValue[1]) {
      // User completes the date range
      setCalendarOpen(false);
      let start = (newValue[0] || firstDate)!.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      let end = newValue[1]!.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      let dates: string[] = [];

      while (start <= end) {
        const dateTimes: string[] = patient.dates.map((date: string) => DateTime.fromISO(date, { setZone: true }).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })).map((date: DateTime) => date.toISO()!);
        const newStart = start.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        console.log("dateTimes: ", dateTimes);
        console.log("newStart: ", newStart);
        if (!dateTimes.includes(newStart.toISO()!) && start.weekday !== 6 && start.weekday !== 7) { 
          // Exclude weekends (Saturday is 6, Sunday is 7)
          dates.push(start.toISO()!);
        }
        start = start.plus({ days: 1 });
      }

      console.log("appointment dates: ", [...patient.dates, ...dates.filter(date => !patient.dates.includes(date))]);
      patient.dates = Array.from(new Set([...patient.dates, ...dates.filter(date => !patient.dates.includes(date))]));
      setValue([null, null]); // Reset date range
    }
  };

  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event;
    const valueArray = typeof value === 'string' ? value.split(',') : value;
    const filteredValues = valueArray.filter((v: any) => v.trim() !== '');
    setPatient({ ...patient, pcpName: filteredValues.join(',') });
  };

  const handleRemoveDate = (index: number) => {
    const newDates = patient.dates.filter((_, idx) => idx !== index);
    setPatient({ ...patient, dates: newDates });
  };

  return (
    <Modal open={open} onClose={() => onClose(false)}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 400,
        borderRadius: 2,
        outline: 'none',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
          Edit Patient
        </Typography>
        <br />
        <TextField
          label="Patient name"
          variant="outlined"
          fullWidth
          value={patient.name}
          onChange={(e: any) => setPatient({ ...patient, name: e.target.value })}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="pcpName-label">Primary provider</InputLabel>
          <Select
            labelId="pcpName-label"
            id="pcpName"
            multiple
            value={patient.pcpName.split(',') || Choices.noPreference}
            label="Primary provider"
            onChange={handleChange}
            renderValue={(selected) => selected.join(', ')}
          >
            { physicianNames.map((pt: string) => (
              <MenuItem value={pt} key={pt}>{pt}</MenuItem>
            )) }
            <MenuItem value={Choices.noPreference}>No preference</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Cannot be edited. To edit, please delete this patient and create a new one with a different number.">
          <TextField
            label="Patient phone number"
            variant="outlined"
            fullWidth
            value={patient.patientPhoneNumber}
            onChange={(e: any) => setPatient({ ...patient, patientPhoneNumber: e.target.value })}
            sx={{ mb: 2 }}
            InputProps={{
              readOnly: true,
            }}
          />
        </Tooltip>
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DateRangePicker
              value={value}
              onChange={handleCalendarChange}
              localeText={{ start: 'Start date', end: 'End date' }}
            />
          </LocalizationProvider>
        </Box>
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {patient.dates.map((date: string, index: number) => (
            <ListItem
              key={index}
              secondaryAction={
                <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveDate(index)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText primary={formatIsoToCustomDateStringWithEEEE(date)} />
            </ListItem>
          ))}
        </List>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="appointment-length-label">Length</InputLabel>
          <Select
            labelId="appointment-length-label"
            id="appointment-length"
            value={patient.appointmentLength}
            onChange={(e: any) => setPatient({ ...patient, appointmentLength: parseInt(e.target.value) })}
          >
            <MenuItem value={30}>30 mins</MenuItem>
            <MenuItem value={40}>40 mins</MenuItem>
            <MenuItem value={45}>45 mins</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="priority-label">Priority</InputLabel>
          <Select
            labelId="priority-label"
            id="priority"
            value={String(patient.priority)}
            onChange={(e: any) => setPatient({ ...patient, priority: e.target.value === 'true' })}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="shiftOptions-label">AM/PM</InputLabel>
          <Select
            labelId="shiftOptions-label"
            id="shiftOptions"
            value={patient.prefersShift}
            onChange={(e: any) => setPatient({ ...patient, prefersShift: e.target.value as ShiftOptions })}
          >
            <MenuItem value={ShiftOptions.AM}>AM</MenuItem>
            <MenuItem value={ShiftOptions.PM}>PM</MenuItem>
            <MenuItem value={ShiftOptions.anyShift}>AM/PM</MenuItem>
          </Select>
        </FormControl>
        <StyledButton onClick={onSuccess} variant="contained">Upload changes</StyledButton>
        <Typography color={Colors.otherColor1} sx={{ mt: 2 }}>{errorMessage}</Typography>
        <Typography color={Colors.otherColor1} sx={{ mt: 2 }}>{localErrorMessage}</Typography>
        {loading && <LoadingWithSubtitle subtitle="Updating patient..." size={40} />}
      </Box>
    </Modal>
  );
};

export default EditPatientModal;
